'use client';

import 'maplibre-gl/dist/maplibre-gl.css';
import '~/assets/components/StadiaAttribution.css';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactMapGlMap, { AttributionControl, Layer, type MapRef, Marker, NavigationControl, Source } from 'react-map-gl/maplibre';
import Supercluster from 'supercluster';
import { useIsDesktop } from '~/hooks/useIsDesktop';
// Locale-stripped, so the map remount key is `build`/`review` on every locale, not `/en-USbuild`.
import { usePathname } from '~/i18n/navigation';
import type { Place, Places } from '~/libs/dato/queries/places';
import type { PlaygroundPlace } from '~/types/Playground.types';
import { type AiTravelPlannerPlace, getEnglishPlaceName, usePlaygroundActions, usePlaygroundaiTravelPlannerResponse, usePlaygroundCurrentSkeletonTrip, usePlaygroundDestinations, usePlaygroundEndPlace, usePlaygroundEnglishPlaceNameByPlaceId, usePlaygroundOrigin, usePlaygroundSelectedDestinations, usePlaygroundUIState } from '../../stores/playgroundStore';
import { baseMapStyle } from '../map/mapStyles/outdoors';
import { ClusterMarker, MapMarker, MapOverviewMarker } from './MapMarkers/MapMarkers';
import { popularLegs, routeLayers } from './MapStyles/routeData';
import { MAP_CONSTANTS, type MapBounds } from './mapConstants';
import { calculateClusters, convertStartEndPlaceToCmsPlace, generateCurveGeoJson, getMapOverview, type MapOverviewItem } from './mapHelpers';
import { PlaceCardDialog } from './PlaceCard/PlaceCardDialog';
function StadiaPlaygroundMap() {
  // playground store state hooks
  const {
    cmsDestinations
  } = usePlaygroundDestinations();
  const selectablePlaces = cmsDestinations.selectable.cmsPlaces;
  const unselectablePlaces = cmsDestinations.unselectable.cmsPlaces;
  const selectedPlaces = usePlaygroundSelectedDestinations().cmsPlaces;
  const {
    dpOrigin: origin,
    cmsOrigin
  } = usePlaygroundOrigin();
  const {
    dpEndPlace: endPlace,
    cmsEndPlace
  } = usePlaygroundEndPlace();
  const englishPlaceNameByPlaceId = usePlaygroundEnglishPlaceNameByPlaceId();
  const skeletonTrip = usePlaygroundCurrentSkeletonTrip();
  // If full trip exists but has no items, it failed - fall back to skeleton for display
  const {
    setIsLoading,
    setShowAnimLoader,
    setIsDragging,
    setCountryExtents
  } = usePlaygroundActions();
  const {
    showRoutes,
    isDragging,
    countryExtents
  } = usePlaygroundUIState();
  const aiTravelPlannerResponse = usePlaygroundaiTravelPlannerResponse();
  const pathname = usePathname();
  const path = pathname?.replace('/map/', '');
  const mapRef = useRef<MapRef | null>(null);
  const isDesktop = useIsDesktop();
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const selectableSupercluster = useRef(new Supercluster({
    radius: MAP_CONSTANTS.CLUSTER_RADIUS
  }));
  const unselectableSupercluster = useRef(new Supercluster({
    radius: MAP_CONSTANTS.CLUSTER_RADIUS
  }));
  const aiPlacesRef = useRef<AiTravelPlannerPlace[]>([]);
  const isAiSearchActiveRef = useRef(false);
  const aiTravelPlannerPlaces: AiTravelPlannerPlace[] = useMemo(() => {
    const response = aiTravelPlannerResponse;
    if (!response?.Places || !response?.PlacesResponse) return [];
    const result = response.PlacesResponse.map<AiTravelPlannerPlace | null>(aiPlaceData => {
      // The webhook is matched against/returns always-English names (see englishPlaceNameByPlaceId),
      // independent of the UI's display locale - so match on that instead of the localized placeName.
      const matchesName = (place: (typeof selectablePlaces)[number]) => getEnglishPlaceName(englishPlaceNameByPlaceId, place) === aiPlaceData.Name;

      // Prefer selectable places; fall back to unselectable for gray sparkles
      let matchingPlace = selectablePlaces.find(matchesName);
      let _isUnreachable = false;
      if (!matchingPlace) {
        matchingPlace = unselectablePlaces.find(matchesName);
        if (!matchingPlace) {
          // If not found anywhere, we can't render it (no coords)
          return null;
        }
        _isUnreachable = true;
      }
      const placeObj: AiTravelPlannerPlace = {
        ...matchingPlace,
        reasons: aiPlaceData.Reasons || [],
        suggestions: aiPlaceData.Suggestions || '',
        tags: aiPlaceData.Tags || [],
        name: aiPlaceData.Name || ''
      };
      return placeObj;
    }).filter((p): p is AiTravelPlannerPlace => p !== null);
    aiPlacesRef.current = result;
    return result;
  }, [selectablePlaces, unselectablePlaces, aiTravelPlannerResponse, englishPlaceNameByPlaceId]);

  // Ensure the ref is always updated when AI places change
  useEffect(() => {
    aiPlacesRef.current = aiTravelPlannerPlaces;
  }, [aiTravelPlannerPlaces]);

  // Ensure the ref is always updated when AI places change
  useEffect(() => {
    aiPlacesRef.current = aiTravelPlannerPlaces;
  }, [aiTravelPlannerPlaces]);

  // Filter out webhook places from regular clusters
  const filteredSelectablePlaces = useMemo(() => {
    if (!aiTravelPlannerPlaces.length) return selectablePlaces;

    // place.name here is the webhook's (always-English) Name, not the localized placeName -
    // compare against the same English name to correctly exclude already-shown AI places.
    const webhookPlaceNames = aiTravelPlannerPlaces.map(place_0 => place_0?.name).filter(Boolean);
    return selectablePlaces.filter(place_1 => !webhookPlaceNames.includes(englishPlaceNameByPlaceId.get(place_1.placeId ?? -1) ?? place_1.placeName));
  }, [selectablePlaces, aiTravelPlannerPlaces, englishPlaceNameByPlaceId]);
  const selectableClusters = useMemo(() => calculateClusters(selectableSupercluster, filteredSelectablePlaces, mapRef), [filteredSelectablePlaces]);
  const unselectableClusters = useMemo(() => calculateClusters(unselectableSupercluster, unselectablePlaces, mapRef), [unselectablePlaces]);
  const tripOverview = skeletonTrip ? getMapOverview(skeletonTrip.items) : null;
  ////// START UPDATE MAP BOUNDS (map movement / zooming code) //////

  // helper for fitting bounds - makes zoom
  const fitBounds = (bounds: MapBounds, overallPadding?: number, maxZoom?: number) => {
    if (!mapRef.current) return;
    const defaultPadding = isDesktop ? 80 : 50;
    const padding = overallPadding ?? defaultPadding;
    const isReview = path === 'review';
    const finalPadding = {
      top: padding,
      // accounts for trip carousel on /review step in mobile
      bottom: Math.max(isReview && !isDesktop ? 200 : padding),
      left: padding,
      right: padding
    };
    const finalMaxZoom = maxZoom ?? 7;
    mapRef.current.fitBounds([[bounds.topLeft.longitude, bounds.topLeft.latitude], [bounds.bottomRight.longitude, bounds.bottomRight.latitude]], {
      duration: 1500,
      padding: finalPadding,
      maxZoom: finalMaxZoom
    });
  };

  // Listen for map zoom events from outside the map context
  useEffect(() => {
    const handleMapZoomOut = (event: CustomEvent) => {
      if (mapRef.current) {
        const {
          zoom,
          duration
        } = event.detail;
        mapRef.current.flyTo({
          zoom,
          duration
        });
      }
    };

    // CHORE: repeats much code from updateMapBounds below - refactor
    const handleMapZoomToResults = (event_0: CustomEvent) => {
      if (mapRef.current) {
        const {
          duration: duration_0
        } = event_0.detail;

        // Only zoom to results if AI search is active
        if (!isAiSearchActiveRef.current) {
          return;
        }

        // Get current AI travel planner places from the ref
        const currentAiPlaces = aiPlacesRef.current;

        // Only zoom to results if we have AI search results
        if (currentAiPlaces.length > 0) {
          // Calculate bounds for AI search results
          const bounds_0 = {
            min: {
              lat: 90.0,
              lng: 180.0
            },
            max: {
              lat: -90.0,
              lng: -180.0
            }
          };
          currentAiPlaces.forEach(place_2 => {
            if (place_2) {
              bounds_0.min.lat = Math.min(bounds_0.min.lat, place_2.location.latitude);
              bounds_0.max.lat = Math.max(bounds_0.max.lat, place_2.location.latitude);
              bounds_0.min.lng = Math.min(bounds_0.min.lng, place_2.location.longitude);
              bounds_0.max.lng = Math.max(bounds_0.max.lng, place_2.location.longitude);
            }
          });

          // Add minimal padding to the bounds to keep content focused
          const padding_0 = 0.05; // 5% padding instead of 10%

          const latPadding = (bounds_0.max.lat - bounds_0.min.lat) * padding_0;
          const lngPadding = (bounds_0.max.lng - bounds_0.min.lng) * padding_0;
          bounds_0.min.lat -= latPadding;
          bounds_0.max.lat += latPadding;
          bounds_0.min.lng -= lngPadding;
          bounds_0.max.lng += lngPadding;

          // Fly to the bounds of AI search results
          mapRef.current.fitBounds([[bounds_0.min.lng, bounds_0.min.lat], [bounds_0.max.lng, bounds_0.max.lat]], {
            duration: duration_0,
            padding: {
              top: 130,
              // Account for floating search bar
              bottom: 20,
              // Reduce bottom padding to focus on content
              left: 50,
              right: 50
            },
            maxZoom: 10
          });

          // Reset the AI search active flag after zooming
          isAiSearchActiveRef.current = false;
        } else {
          // Fallback: zoom back to a reasonable view if no AI results
          mapRef.current.flyTo({
            zoom: 6,
            duration: duration_0
          });

          // Reset the AI search active flag
          isAiSearchActiveRef.current = false;
        }
      }
    };
    const handleAiSearchActive = (event_1: CustomEvent) => {
      isAiSearchActiveRef.current = event_1.detail.active;
    };
    const handleMapZoomToDefault = (event_2: CustomEvent) => {
      if (mapRef.current) {
        const {
          duration: _duration
        } = event_2.detail;

        // Reset AI search active flag
        isAiSearchActiveRef.current = false;

        // Zoom to Europe bounds specifically when no AI results or error
        fitBounds(MAP_CONSTANTS.EUROPE_BOUNDS, undefined, undefined);
      }
    };
    window.addEventListener('playground-map-zoom-out', handleMapZoomOut as EventListener);
    window.addEventListener('playground-map-zoom-to-results', handleMapZoomToResults as EventListener);
    window.addEventListener('playground-ai-search-active', handleAiSearchActive as EventListener);
    window.addEventListener('playground-map-zoom-to-default', handleMapZoomToDefault as EventListener);
    return () => {
      window.removeEventListener('playground-map-zoom-out', handleMapZoomOut as EventListener);
      window.removeEventListener('playground-map-zoom-to-results', handleMapZoomToResults as EventListener);
      window.removeEventListener('playground-ai-search-active', handleAiSearchActive as EventListener);
      window.removeEventListener('playground-map-zoom-to-default', handleMapZoomToDefault as EventListener);
    };
  }, []); // Remove dependency to prevent recreation of event listeners

  //// Main updating function that calculates bounds and zooms us (when called by useEffect)
  const updateMapBounds = () => {
    if (!mapRef.current) return;

    // country extents override zoom behaviour on land only
    if (countryExtents) {
      fitBounds(countryExtents);
      setCountryExtents(null);
      return;
    }
    const newBounds: MapBounds = {
      topLeft: {
        latitude: 90.0,
        longitude: 90.0
      },
      bottomRight: {
        latitude: -90.0,
        longitude: -90.0
      }
    };
    const isDefaultBounds = () => newBounds.topLeft.latitude === 90.0 && newBounds.bottomRight.latitude === -90.0 && newBounds.topLeft.longitude === 90.0 && newBounds.bottomRight.longitude === -90.0;

    // Update bounds for a single location
    const includeLocationInNewBounds = (latitude: number, longitude: number) => {
      newBounds.topLeft.latitude = Math.min(newBounds.topLeft.latitude, latitude);
      newBounds.topLeft.longitude = Math.min(newBounds.topLeft.longitude, longitude);
      newBounds.bottomRight.latitude = Math.max(newBounds.bottomRight.latitude, latitude);
      newBounds.bottomRight.longitude = Math.max(newBounds.bottomRight.longitude, longitude);
    };

    // take lat and lng for both topLeft and bottomRight and update bounds for those

    // Update bounds for CMS places
    const includePlacesInNewBounds = (places: Places['allPlaces']) => {
      places.forEach(({
        location
      }) => {
        includeLocationInNewBounds(location.latitude, location.longitude);
      });
    };
    const includeOriginInNewBounds = (origin_0: PlaygroundPlace) => {
      includeLocationInNewBounds(origin_0.geo.latitude, origin_0.geo.longitude);
    };
    const includeEndPlaceInNewBounds = (endPlace_0: PlaygroundPlace) => {
      includeLocationInNewBounds(endPlace_0.geo.latitude, endPlace_0.geo.longitude);
    };
    const includeTripOverviewInNewBounds = (tripOverview_0: MapOverviewItem[]) => {
      tripOverview_0.forEach(item => {
        if (!item?.geoCoordinates) return;
        includeLocationInNewBounds(item.geoCoordinates.latitude, item.geoCoordinates.longitude);
      });
    };
    // Calculate bounds for all relevant elements
    if (selectablePlaces.length > 0 && path === 'build') {
      includePlacesInNewBounds(selectablePlaces);
    }
    if (selectedPlaces.length > 0 && path === 'build') {
      includePlacesInNewBounds(selectedPlaces);
    }
    if (origin && !MAP_CONSTANTS.MAP_CONFIG.REMOVE_ORIGIN_FROM_DEFAULT_BOUNDS && path === 'build') {
      includeOriginInNewBounds(origin);
      if (endPlace) includeEndPlaceInNewBounds(endPlace);
    }
    if (tripOverview && path === 'review') {
      includeTripOverviewInNewBounds(tripOverview);
    }
    if (isDefaultBounds()) {
      fitBounds(MAP_CONSTANTS.EUROPE_BOUNDS);
    } else {
      fitBounds(newBounds);
    }
  };
  useEffect(() => {
    // Update bounds on:
    // 1. Initial load - using mapBounds if available
    // 2. When trips change (new trips generated, or selected in /review)
    if (isMapLoaded) updateMapBounds();
  }, [skeletonTrip, isMapLoaded]);

  ////// END UPDATE MAP BOUNDS CODE //////

  // prevent clicks when dragging
  const handleDragStart = () => {
    setIsDragging(true);
  };
  const handleDragEnd = () => {
    setIsDragging(false);
  };
  const onLoad = () => {
    setIsLoading(false);
    setShowAnimLoader(false);
    setIsMapLoaded(true);
  };
  const onUnmount = useCallback(() => {
    selectableSupercluster.current = new Supercluster({
      radius: MAP_CONSTANTS.CLUSTER_RADIUS
    });
    unselectableSupercluster.current = new Supercluster({
      radius: MAP_CONSTANTS.CLUSTER_RADIUS
    });
    setIsMapLoaded(false);
    mapRef.current = null;
  }, []);
  return <ReactMapGlMap onLoad={onLoad} onRemove={onUnmount} reuseMaps={MAP_CONSTANTS.MAP_CONFIG.REUSE_MAPS} initialViewState={MAP_CONSTANTS.INITIAL_VIEW_STATE} onMoveStart={handleDragStart} onMoveEnd={handleDragEnd} dragRotate={MAP_CONSTANTS.MAP_CONFIG.DRAG_ROTATE} // Optional: disable rotation to simplify drag detection
  interactiveLayerIds={isDragging ? [] : undefined} // Disable interactions while dragging
  maxZoom={12} ref={mapRef} style={{
    width: '100%',
    flex: '1 1 auto'
  }} scrollZoom={true} mapStyle={baseMapStyle as any} attributionControl={false}>
      {path === 'build' && selectableClusters && selectableClusters.filter(feature => feature?.properties && feature.properties.id !== origin?.id && !aiTravelPlannerPlaces.some(aiPlace => aiPlace?.id === feature.properties?.id)).map((feature_0, i) => {
      if (!feature_0 || !feature_0.geometry || !feature_0.properties) return null;
      const lng = feature_0.geometry.coordinates[0];
      const lat = feature_0.geometry.coordinates[1];
      const isAlreadyAdded = feature_0.properties && selectedPlaces.some(cmsPlace => cmsPlace.id === feature_0.properties?.id);
      const isCluster = feature_0.properties.cluster;
      if (isAlreadyAdded) return null;
      if (isCluster) return <Marker key={`${i}-box`} longitude={lng} latitude={lat} style={{
        zIndex: MAP_CONSTANTS.MARKER_Z_INDEX.CLUSTER
      }}>
                  <ClusterMarker isUnreachable={false} clickHandler={() => {
          if (feature_0.properties?.cluster && mapRef.current) {
            const expansionZoom = selectableSupercluster.current.getClusterExpansionZoom(feature_0.properties.cluster_id);
            mapRef.current.flyTo({
              center: [lng, lat],
              zoom: expansionZoom + MAP_CONSTANTS.EXPANSION_ZOOM_ADDED,
              duration: 500
            });
          }
        }} pointCount={feature_0.properties.point_count} />
                </Marker>;
      if (!isCluster) return <Marker key={`${i}-box`} longitude={lng} latitude={lat} style={{
        zIndex: MAP_CONSTANTS.MARKER_Z_INDEX.BASE
      }}>
                  <MapMarker key={`${i}-selectable-place-marker`} isAdded={isAlreadyAdded} place={feature_0.properties as Place['place']} />
                </Marker>;
    })}
      {path === 'build' && unselectableClusters && unselectableClusters.filter(place_3 => place_3?.properties && place_3.properties.id !== origin?.id &&
    // Exclude places that are represented by AI sparkle markers
    !aiTravelPlannerPlaces.some(aiPlace_0 => aiPlace_0?.id === place_3.properties?.id)).map((place_4, i_0) => {
      if (!place_4 || !place_4.geometry || !place_4.properties) return null;
      const lng_0 = place_4.geometry.coordinates[0];
      const lat_0 = place_4.geometry.coordinates[1];
      const isAlreadyAdded_0 = place_4.properties && selectedPlaces.some(cmsPlace_0 => cmsPlace_0.id === place_4.properties?.id);
      const isCluster_0 = place_4.properties?.cluster;
      if (isAlreadyAdded_0) return null;
      if (isCluster_0) return <Marker key={`${i_0}-box`} longitude={lng_0} latitude={lat_0} style={{
        zIndex: MAP_CONSTANTS.MARKER_Z_INDEX.UNSELECTABLE
      }}>
                  <ClusterMarker pointCount={place_4.properties.point_count} isUnreachable clickHandler={() => {
          if (place_4.properties?.cluster && mapRef.current) {
            const expansionZoom_0 = unselectableSupercluster.current.getClusterExpansionZoom(place_4.properties.cluster_id);
            mapRef.current.flyTo({
              center: [lng_0, lat_0],
              zoom: expansionZoom_0 + MAP_CONSTANTS.EXPANSION_ZOOM_ADDED,
              duration: 500
            });
          }
        }} />
                </Marker>;
      if (!isCluster_0) return <Marker key={`${i_0}-box`} longitude={lng_0} latitude={lat_0} style={{
        zIndex: MAP_CONSTANTS.MARKER_Z_INDEX.UNSELECTABLE
      }}>
                  <MapMarker key={`${i_0}-unselectable-place-marker`} place={place_4.properties as Place['place']} isUnreachable />
                </Marker>;
    })}
      {path === 'build' && selectedPlaces.length && selectedPlaces.filter(destination => !aiTravelPlannerPlaces.some(aiPlace_1 => aiPlace_1?.id === destination.id)).map((destination_0, i_1) => {
      const lng_1 = destination_0.location.longitude;
      const lat_1 = destination_0.location.latitude;
      return <Marker key={`${i_1}-selected-place-marker`} longitude={lng_1} latitude={lat_1} style={{
        zIndex: MAP_CONSTANTS.MARKER_Z_INDEX.SELECTED
      }}>
                <MapMarker place={destination_0} isAdded />
              </Marker>;
    })}
      {/* ORIGIN PIN */}
      {origin && path === 'build' && <Marker key={`origin-marker-box`} longitude={origin.geo.longitude} latitude={origin.geo.latitude} style={{
      zIndex: MAP_CONSTANTS.MARKER_Z_INDEX.START_END
    }}>
          <MapMarker variant="start" place={convertStartEndPlaceToCmsPlace(origin, cmsOrigin?.placeName)} />
        </Marker>}

      {/* END PLACE PIN */}
      {endPlace && path === 'build' && <Marker key={`destination-marker-box`} longitude={endPlace.geo.longitude} latitude={endPlace.geo.latitude} style={{
      zIndex: MAP_CONSTANTS.MARKER_Z_INDEX.START_END
    }}>
          <MapMarker variant="end" place={convertStartEndPlaceToCmsPlace(endPlace, cmsEndPlace?.placeName)} />
        </Marker>}

      {/* WEBHOOK RESPONSE PLACES - Show on top of other markers */}
      {path === 'build' && aiTravelPlannerPlaces.length > 0 && aiTravelPlannerPlaces.map((place_5, i_2) => {
      if (!place_5) return null;
      const isSelected = selectedPlaces.some(selected => selected.id === place_5.id);
      return <Marker key={`webhook-${i_2}-marker`} longitude={place_5.location.longitude} latitude={place_5.location.latitude} style={{
        zIndex: MAP_CONSTANTS.MARKER_Z_INDEX.SPARKLE
      }}>
              {isSelected ? <MapMarker place={place_5} isAdded={true} /> : <MapMarker isSparkle={true} place={place_5} />}
            </Marker>;
    })}

      {/* REVIEW STEP ONLY */}
      {path === 'review' && skeletonTrip && tripOverview && generateCurveGeoJson(tripOverview.filter(item_0 => item_0.geoCoordinates) as any)?.features.map((feature_1, featureIndex) => {
      const tripLineId = `trip-line-${featureIndex}`;
      const tripBackgroundId = `trip-background-${featureIndex}`;
      const tripArrowId = `trip-arrow-${featureIndex}`;
      const start = tripOverview[0];
      const end = tripOverview[tripOverview.length - 1];
      const middleStays = tripOverview.slice(1, -1);
      return <div key={`trip-data-${featureIndex}`}>
                <Source id={`trip-data-${featureIndex}`} type="geojson" data={feature_1}>
                  <Layer id={tripBackgroundId} {...MAP_CONSTANTS.LINE_STYLES.HALO} type="line" />
                  <Layer id={tripLineId} {...MAP_CONSTANTS.LINE_STYLES.MAIN} type="line" />
                  <Layer id={tripArrowId} type="symbol" layout={{
            'symbol-placement': 'line',
            'symbol-spacing': 75,
            'symbol-avoid-edges': true,
            'icon-image': 'triangle_11',
            'icon-size': 1.1,
            'icon-allow-overlap': false,
            'icon-ignore-placement': false,
            'icon-rotation-alignment': 'map',
            'icon-text-fit': 'none',
            'icon-rotate': 90
          }} paint={{
            'icon-color': '#000000'
          }} />
                </Source>
                {start?.geoCoordinates && <Marker key={`origin-marker-box`} longitude={start.geoCoordinates.longitude} latitude={start.geoCoordinates.latitude}>
                    <MapOverviewMarker overviewItem={start} variant="start" />
                  </Marker>}
                {middleStays.map(overviewItem => {
          if (overviewItem.type !== 'Stay' || !overviewItem.geoCoordinates) return null;

          // Find the matching CMS place for this stay
          const matchingPlace_0 = cmsDestinations.all.cmsPlaces.find(place_6 => place_6.placeName === overviewItem.title);

          // If we can't find a matching place, fall back to MapOverviewMarker
          if (!matchingPlace_0) {
            return <Marker key={`trip-${overviewItem.title}-outbound-${overviewItem.id}-marker`} longitude={overviewItem.geoCoordinates.longitude} latitude={overviewItem.geoCoordinates.latitude}>
                        <MapOverviewMarker overviewItem={overviewItem} variant="default" />
                      </Marker>;
          }
          return <Marker key={`trip-${overviewItem.title}-outbound-${overviewItem.id}-marker`} longitude={overviewItem.geoCoordinates.longitude} latitude={overviewItem.geoCoordinates.latitude}>
                      <MapMarker place={matchingPlace_0} isAdded={true} customZoomOffset={[0, -240]} isReviewPage={true} overviewItem={overviewItem} />
                    </Marker>;
        })}
                {end && end.title !== start.title && end.geoCoordinates && <Marker key={`destination-marker-box`} longitude={end.geoCoordinates.longitude} latitude={end.geoCoordinates.latitude}>
                    <MapOverviewMarker overviewItem={end} variant="end" />
                  </Marker>}
              </div>;
    })}

      {/* Popular Legs */}
      {showRoutes && <Source id="route-source" type="geojson" data={popularLegs}>
          {routeLayers.map(layer => <Layer key={layer.id} {...layer as any} />)}
        </Source>}

      <AttributionControl compact={false} style={{
      position: 'relative',
      bottom: isDesktop ? '5px' : '0px'
    }} />
      <NavigationControl showCompass={false} showZoom={true} position="bottom-right" />

      <PlaceCardDialog />
    </ReactMapGlMap>;
}
export { StadiaPlaygroundMap };